<template>
  <div>
    <div class="mb-6">
      <div class="text-xl">
        {{ t("memberOnboarding.title", { accountName: account?.name ?? "" }) }}
      </div>
      <div class="text-sm text-medium-emphasis">{{ t("memberOnboarding.description") }}</div>
    </div>

    <div v-if="loading" class="flex justify-center py-8">
      <v-progress-circular indeterminate color="primary" width="2" size="24" />
    </div>
    <template v-else-if="accountUser?.onboardingSubmittedAt">
      <v-alert
        v-if="member?.user.kyc?.kycStatus === 'REFUSED'"
        type="error"
        variant="tonal"
        :title="t('memberOnboarding.refused.title')"
        :text="t('memberOnboarding.refused.message')"
      />
      <OpeningCard
        v-else
        :title="t('memberOnboarding.inReview.title')"
        :subtitle="t('memberOnboarding.inReview.message')"
      >
        <v-icon icon="mdi-account-clock-outline" size="48" color="primary" />
      </OpeningCard>
    </template>
    <v-stepper
      v-else-if="member"
      :model-value="step"
      class="bg-transparent"
      flat
      editable
      hide-actions
      mobile-breakpoint="md"
      @update:model-value="goTo"
    >
      <v-stepper-header class="shadow-none">
        <template v-for="(item, index) in steps" :key="item.value">
          <v-divider v-if="index > 0" />
          <v-stepper-item
            :value="item.value"
            :title="item.title"
            :complete="item.complete"
            :error="item.missing"
            :subtitle="item.missing ? t('treezorOnboarding.steps.missingInformation') : undefined"
            :color="item.complete ? 'success' : 'primary'"
          />
        </template>
      </v-stepper-header>

      <!-- Padding keeps step card shadows inside the window's overflow clip. -->
      <v-stepper-window class="mx-0 mb-0 p-1">
        <v-stepper-window-item :value="1">
          <v-alert v-if="identityLocked" class="mb-6" type="success" variant="tonal">
            {{ t("memberOnboarding.identityVerified") }}
          </v-alert>
          <v-form
            id="member-form"
            ref="memberForm"
            :disabled="saving || identityLocked"
            class="flex flex-col gap-6"
            validate-on="invalid-input lazy"
            @submit.prevent="save"
          >
            <OpeningCard :title="t('treezorOnboarding.accountCreator.sections.identity')">
              <PersonIdentityFields
                v-model="form"
                :missing-fields="leftSteps.has(1) ? missingFields : []"
              >
                <template #prepend>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="form.title"
                      :items="
                        (['M', 'MME', 'MX'] as const).map((value) => ({
                          value,
                          title: t(`memberOnboarding.civilities.${value}`),
                        }))
                      "
                      :rules="[required]"
                    >
                      <template #label>
                        {{ t("memberOnboarding.civility") }}
                        <span class="text-error">*</span>
                      </template>
                    </v-select>
                  </v-col>
                </template>
                <v-col cols="12">
                  <div class="text-medium-emphasis">
                    {{ t("treezorOnboarding.accountCreator.specifiedUSPersonQuestion") }}
                    <span class="text-error">*</span>
                  </div>
                  <v-radio-group
                    v-model="form.specifiedUSPerson"
                    color="primary"
                    inline
                    hide-details="auto"
                    :aria-label="t('treezorOnboarding.accountCreator.specifiedUSPersonQuestion')"
                  >
                    <v-radio :label="t('treezorOnboarding.sanctions.yes')" :value="true" />
                    <v-radio :label="t('treezorOnboarding.sanctions.no')" :value="false" />
                  </v-radio-group>
                </v-col>
              </PersonIdentityFields>
            </OpeningCard>
            <OpeningCard :title="t('treezorOnboarding.sections.contact')">
              <PersonContactFields
                v-model="form"
                :missing-fields="leftSteps.has(1) ? missingFields : []"
              />
            </OpeningCard>
          </v-form>
          <div class="mt-6 flex justify-end gap-2">
            <v-btn
              v-if="!identityLocked"
              type="submit"
              form="member-form"
              color="primary"
              variant="flat"
              prepend-icon="mdi-content-save"
              append-icon="mdi-chevron-right"
              :loading="saving"
              :disabled="saving"
            >
              {{ t("common.next") }}
            </v-btn>
            <v-btn
              v-else
              color="primary"
              variant="flat"
              append-icon="mdi-chevron-right"
              @click="goTo(2)"
            >
              {{ t("common.next") }}
            </v-btn>
          </div>
        </v-stepper-window-item>

        <v-stepper-window-item :value="2">
          <OpeningCard
            :title="t('treezorOnboarding.accountCreator.liveness.title')"
            :subtitle="t('treezorOnboarding.accountCreator.liveness.hint')"
          >
            <v-chip
              v-if="member.kycLivenessStatus === 'verified'"
              size="large"
              color="success"
              variant="tonal"
              prepend-icon="mdi-check"
            >
              {{ t("treezorOnboarding.accountCreator.liveness.verified") }}
            </v-chip>
            <template v-else>
              <div
                v-if="member.kycLivenessStatus === 'started'"
                class="mb-3 flex items-center gap-2 text-sm text-medium-emphasis"
              >
                <v-progress-circular indeterminate size="20" width="2" color="primary" />
                {{ t("treezorOnboarding.accountCreator.liveness.started") }}
              </div>
              <v-alert
                v-else-if="member.kycLivenessStatus !== 'none'"
                class="mb-3"
                type="error"
                color="error"
              >
                <p class="font-semibold m-0">
                  {{ t(`treezorOnboarding.accountCreator.liveness.${member.kycLivenessStatus}`) }}
                </p>
                <ul v-if="member.kycLivenessReasonCodes.length" class="m-0 ps-4">
                  <li v-for="code in member.kycLivenessReasonCodes" :key="code">
                    {{ t(`treezorOnboarding.accountCreator.liveness.reasons.${code}`) }}
                  </li>
                </ul>
              </v-alert>
              <v-btn
                v-if="member.kycLivenessStatus !== 'started'"
                color="primary"
                variant="flat"
                prepend-icon="mdi-play"
                :disabled="missingFields.length > 0 || !member.user.title"
                @click="startKycLiveness"
              >
                {{
                  t(
                    member.kycLivenessStatus === "none"
                      ? "treezorOnboarding.accountCreator.liveness.start"
                      : "treezorOnboarding.accountCreator.liveness.retry",
                  )
                }}
              </v-btn>
              <p v-if="leftSteps.has(2)" class="text-sm text-error mt-2 mb-0">
                {{ t("treezorOnboarding.accountCreator.liveness.missing") }}
              </p>
            </template>
          </OpeningCard>
          <div class="mt-6 flex justify-end gap-2">
            <v-btn class="me-auto" variant="text" prepend-icon="mdi-chevron-left" @click="goTo(1)">
              {{ t("common.previous") }}
            </v-btn>
            <v-btn color="primary" variant="flat" append-icon="mdi-chevron-right" @click="goTo(3)">
              {{ t("common.next") }}
            </v-btn>
          </div>
        </v-stepper-window-item>

        <v-stepper-window-item :value="3">
          <v-alert
            v-if="!member.powerOfAttorneyRequired"
            type="success"
            variant="tonal"
            :title="t('memberOnboarding.powerOfAttorney.title')"
            :text="t('memberOnboarding.powerOfAttorney.notRequired')"
          />
          <OpeningCard
            v-else
            :title="t('memberOnboarding.powerOfAttorney.title')"
            :subtitle="t('memberOnboarding.powerOfAttorney.hint')"
          >
            <div class="flex flex-col gap-3">
              <v-card
                v-if="member.powerOfAttorneyDocument"
                variant="outlined"
                rounded="lg"
                class="flex items-center gap-2 px-3 py-3 border-theme sm:gap-3 sm:px-4"
              >
                <v-icon icon="mdi-file-document-outline" class="text-medium-emphasis" />
                <div class="grow min-w-0">
                  <div class="text-sm font-medium">
                    {{ t("treezorOnboarding.documents.powerOfAttorney") }}
                  </div>
                  <div class="text-xs text-medium-emphasis">
                    {{
                      t("treezorOnboarding.documents.uploadedOn", {
                        date: new Date(member.powerOfAttorneyDocument.createdAt).toLocaleString(
                          locale,
                          { dateStyle: "short", timeStyle: "short" },
                        ),
                      })
                    }}
                  </div>
                </div>
              </v-card>
              <v-file-input
                v-model="powerOfAttorneyFile"
                density="compact"
                variant="outlined"
                prepend-inner-icon="mdi-paperclip"
                :prepend-icon="''"
                hide-details="auto"
                accept=".jpg,.jpeg,.png,.svg,.tif,.tiff,.pdf,image/jpeg,image/png,image/svg+xml,image/tiff,application/pdf"
                :rules="[maxFileSize(50)]"
                :error-messages="
                  leftSteps.has(3) && !member.powerOfAttorneyDocument ? t('errors.required') : []
                "
                :loading="uploading"
                :disabled="uploading || submitting"
                :label="t('treezorOnboarding.documents.chooseFile')"
                @update:model-value="uploadPowerOfAttorney"
              />
            </div>
          </OpeningCard>
          <div class="mt-6 flex justify-end gap-2">
            <v-btn
              class="me-auto"
              variant="text"
              prepend-icon="mdi-chevron-left"
              :disabled="submitting"
              @click="goTo(2)"
            >
              {{ t("common.previous") }}
            </v-btn>
            <v-btn
              color="primary"
              variant="flat"
              prepend-icon="mdi-send"
              :loading="submitting"
              :disabled="submitting || uploading"
              @click="submit"
            >
              {{ t("common.send") }}
            </v-btn>
          </div>
        </v-stepper-window-item>
      </v-stepper-window>
    </v-stepper>
  </div>
</template>

<script setup lang="ts">
import { useDocument } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { useOpenInNewTab } from "@/composables/useOpenInNewTab";
import { useRules } from "@/composables/useRules";
import { call, db } from "@/firebase";
import { useToast, useUserStore } from "@/store";
import { isTreezorDocumentFile, postTreezorDocumentUpload } from "@/utils/treezorDocumentUpload";
import { accountFullPath, type Account } from "@aircap/common/account";
import type { CallablesMap } from "@aircap/common/callables";
import { TREEZOR_KYC_REQUIRED_EFFECTIVE_BENEFICIARY_FIELDS } from "@aircap/common/treezor";
import { captureException } from "@sentry/vue";
import { useIntervalFn } from "@vueuse/core";
import { doc } from "firebase/firestore";
import { computed, ref, watch } from "vue";
import type { VForm } from "vuetify/components";
import OpeningCard from "../Opening/OpeningCard.vue";
import PersonContactFields from "../Opening/PersonContactFields.vue";
import PersonIdentityFields from "../Opening/PersonIdentityFields.vue";
import { accountCreatorFormFrom, emptyAccountCreatorForm } from "../Opening/useTreezorOnboarding";

const props = defineProps<{
  accountId: string;
}>();

const { t, locale } = useI18n();
const user = useUserStore();
const { maxFileSize, required } = useRules();
const { showErrorToast, showSuccessToast } = useToast();
const openInNewTab = useOpenInNewTab();

const account = useDocument<Account>(computed(() => doc(db, accountFullPath(props.accountId))));
const accountUser = computed(() => (user.uid ? account.value?.users?.[user.uid] : undefined));
const member = ref<CallablesMap["getTreezorAccountMember"]["response"]>();
const loading = ref(false);
const form = ref({
  ...emptyAccountCreatorForm(),
  title: null as CallablesMap["updateTreezorAccountMember"]["params"]["title"] | null,
});

const identityLocked = computed(
  () =>
    member.value?.user.kyc?.kycStatus === "PENDING" ||
    member.value?.user.kyc?.kycStatus === "VALIDATED",
);
const missingFields = computed(() =>
  TREEZOR_KYC_REQUIRED_EFFECTIVE_BENEFICIARY_FIELDS.filter((field) => {
    const value = member.value?.user[field];
    return value === undefined || value === null || value === "";
  }),
);

const step = ref(1);
const leftSteps = ref(new Set<number>());
const steps = computed(() =>
  [
    {
      value: 1,
      title: t("treezorOnboarding.steps.you"),
      complete:
        identityLocked.value || (missingFields.value.length === 0 && !!member.value?.user.title),
    },
    {
      value: 2,
      title: t("treezorOnboarding.steps.identity"),
      complete: identityLocked.value || member.value?.kycLivenessStatus === "verified",
    },
    {
      value: 3,
      title: t("memberOnboarding.steps.powerOfAttorney"),
      complete: !member.value?.powerOfAttorneyRequired || !!member.value.powerOfAttorneyDocument,
    },
  ].map((item) => ({ ...item, missing: leftSteps.value.has(item.value) && !item.complete })),
);
const goTo = (target: number) => {
  for (let value = 1; value < target; value++) leftSteps.value.add(value);
  leftSteps.value.add(step.value);
  step.value = target;
};

const load = async () => {
  try {
    member.value = await call("getTreezorAccountMember")({ accountId: props.accountId });
  } catch (cause) {
    const error = new Error(`Error loading member onboarding of account ${props.accountId}`, {
      cause,
    });
    console.error(error);
    captureException(error);
    showErrorToast();
  }
};
watch(
  () => props.accountId,
  async () => {
    step.value = 1;
    leftSteps.value.clear();
    loading.value = true;
    await load();
    loading.value = false;
    if (member.value)
      form.value = {
        ...accountCreatorFormFrom(member.value.user, {
          isLegalRepresentative: false,
          isEffectiveBeneficiary: false,
        }),
        title:
          member.value.user.title === "" || member.value.user.title === "MLLE"
            ? null
            : member.value.user.title,
      };
    if (identityLocked.value) step.value = 3;
  },
  { immediate: true },
);

const memberForm = ref<VForm>();
const saving = ref(false);
const save = async () => {
  const result = await memberForm.value?.validate();
  if (!result?.valid || !form.value.title) return;

  saving.value = true;
  try {
    await call("updateTreezorAccountMember")({
      accountId: props.accountId,
      specifiedUSPerson: form.value.specifiedUSPerson,
      firstname: form.value.firstname,
      lastname: form.value.lastname,
      middleNames: form.value.middleNames,
      birthday: form.value.birthday,
      placeOfBirth: form.value.placeOfBirth,
      birthCountry: form.value.birthCountry ?? "",
      nationality: form.value.nationality ?? "",
      phone: form.value.phone,
      address1: form.value.address1,
      address2: form.value.address2,
      address3: form.value.address3,
      postcode: form.value.postcode,
      city: form.value.city,
      country: form.value.country ?? "",
      title: form.value.title,
    });
    await load();
    showSuccessToast(t("treezorOnboarding.accountCreator.updateSuccess"));
    goTo(2);
  } catch (cause) {
    const error = new Error(`Error saving member identity on account ${props.accountId}`, {
      cause,
    });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    saving.value = false;
  }
};

let kycLivenessRequestedAt: number | undefined;
let kycLivenessPreviousStartedAt: number | null = null;
const kycLivenessPolling = useIntervalFn(
  () => {
    if (!member.value || !kycLivenessRequestedAt) return;
    if (Date.now() - kycLivenessRequestedAt >= 60 * 60 * 1000) {
      member.value.kycLivenessStatus = "none";
      return;
    }
    call("getTreezorAccountMember")({ accountId: props.accountId })
      .then((result) => {
        // The previous attempt's result can come back until Treezor registers the new one.
        const awaitingNewAttempt =
          kycLivenessRequestedAt !== undefined &&
          Date.now() - kycLivenessRequestedAt < 2 * 60 * 1000 &&
          result.kycLivenessStatus !== "verified" &&
          result.kycLivenessStartedAt === kycLivenessPreviousStartedAt;
        if (!awaitingNewAttempt) member.value = result;
      })
      .catch((cause: unknown) => {
        const error = new Error("Error polling Treezor KYC liveness status", { cause });
        console.error(error);
        captureException(error);
      });
  },
  10_000,
  { immediate: false },
);
watch(
  () => member.value?.kycLivenessStatus,
  (status) => {
    if (status === "started") kycLivenessPolling.resume();
    else kycLivenessPolling.pause();
  },
);

const startKycLiveness = () => {
  if (!member.value || !openInNewTab("treezorKycLiveness", { accountId: props.accountId })) return;
  kycLivenessRequestedAt = Date.now();
  kycLivenessPreviousStartedAt = member.value.kycLivenessStartedAt;
  member.value.kycLivenessStatus = "started";
  member.value.kycLivenessReasonCodes = [];
};

const powerOfAttorneyFile = ref<File | null>(null);
const uploading = ref(false);
const uploadPowerOfAttorney = async (file: File | File[] | null) => {
  if (!file || Array.isArray(file)) return;
  if (!isTreezorDocumentFile(file)) {
    showErrorToast(t("treezorOnboarding.documents.invalidFileType"));
    powerOfAttorneyFile.value = null;
    return;
  }

  uploading.value = true;
  try {
    const upload = await call("createTreezorPowerOfAttorneyUpload")({ accountId: props.accountId });
    await postTreezorDocumentUpload(upload, file);
    // Treezor lists the document a moment after the upload.
    for (let attempt = 0; attempt < 8; attempt++) {
      await load();
      if (member.value?.powerOfAttorneyDocument?.documentId === upload.documentId) break;
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    showSuccessToast(t("treezorOnboarding.documents.uploadSuccess"));
  } catch (cause) {
    const error = new Error(`Error uploading power of attorney on account ${props.accountId}`, {
      cause,
    });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    powerOfAttorneyFile.value = null;
    uploading.value = false;
  }
};

const submitting = ref(false);
const submit = async () => {
  goTo(3);
  if (steps.value.some((item) => !item.complete)) {
    step.value = steps.value.find((item) => !item.complete)?.value ?? 3;
    return;
  }

  submitting.value = true;
  try {
    await call("submitTreezorAccountMemberOnboarding")({ accountId: props.accountId });
    await load();
  } catch (cause) {
    const error = new Error(`Error submitting member onboarding on account ${props.accountId}`, {
      cause,
    });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    submitting.value = false;
  }
};
</script>
